import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import HtmlUtils from 'lib/HtmlUtils';
import { AccountLoginResult } from 'models/ApiEnums';
import * as ApiModels from "models/ApiModels";
import { ApiException } from 'models/ApiModels';
import * as React from 'react';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { RoutePaths } from 'routes/RouteConfig';
import Session from 'services/Session';
import { clearInterval, setInterval } from 'timers';

/////////////////////////////////////////////////////////////////////////////////////////////////

class TokenRenew {
    private _updating: boolean;
    private _newToken: string;

    constructor() {
        this._updating = false;
        this._newToken = null;
    }

    public get isUpdating() {
        return this._updating;
    }

    public get newToken() {
        return this._newToken;
    }

    public startUpdate() {
        this._updating = true;
        this._newToken = null;
    }

    public endUpdate(token: string) {
        this._newToken = token;
        this._updating = false;
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////


export const AxiosErrorTitle: React.FC<{ exceptionType: string; }> = (props) => {
    const { t } = useTranslation();
    var msg = props.exceptionType == null
        ? t("Components.AxiosHandler.UnknowException")
        : props.exceptionType === "AppRuleValidationException"
            ? t("Components.AxiosHandler.AppRuleValidationException")
            : props.exceptionType === "AppException"
                ? t("Components.AxiosHandler.AppException")
                : props.exceptionType && props.exceptionType.toLowerCase().indexOf("exception") > -1
                    ? t("Components.AxiosHandler.AppError")
                    : t("Components.AxiosHandler.AppRequestError");

    return <span>{msg}</span>;
};

/////////////////////////////////////////////////////////////////////////////////////////////////


interface IState {
    error?: ApiException;
}

type props = RouteComponentProps & WithSnackbar & WithTranslation;

class AxiosHandler extends React.Component<props, IState> {

    public state: IState = {
        error: null,
    };

    requestInterceptor: number;
    responseInterceptor: number;

    private tokenRenew = new TokenRenew();

    /** Atualiza o token de autorização */
    private updateToken(): Promise<string> {

        // Garante apenas um request de novo login.
        if (!this.tokenRenew.isUpdating) {

            this.tokenRenew.startUpdate();

            return new Promise<string>((resolve) => {

                var user = Session.cachedUser;
                var refreshToken = Session.refreshToken ? Session.refreshToken.token : null;

                if (user && refreshToken) {
                    const p = JSON.stringify({
                        refreshToken: refreshToken,
                        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    });

                    axios.post<ApiModels.AuthenticationResult>(
                        "/api/v1.0/Session/LoginFromRefreshToken",
                        p,
                        {
                            responseType: "json",
                            headers: { "Content-Type": "application/json" }
                        }
                    )
                        .then((tokenRefreshResponse) => {

                            if (tokenRefreshResponse && tokenRefreshResponse.data.webToken && tokenRefreshResponse.data.status == AccountLoginResult.Valid) {
                                Session.processWebToken(tokenRefreshResponse.data.webToken);
                                this.tokenRenew.endUpdate(tokenRefreshResponse.data.webToken.token);
                                resolve(tokenRefreshResponse.data.webToken.token);

                                // Utilizar este código para ativar o AntiForgery ao revalidar o Token.
                                // const authService = new AuthService();
                                // authService.getAntiForgery()
                                //    .then(() => {
                                //        this.tokenRenew.endUpdate(tokenRefreshResponse.data.user.token);
                                //        resolve(tokenRefreshResponse.data.user.token);
                                //    })
                                //    .catch(() => {
                                //        this.tokenRenew.endUpdate(null);
                                //        resolve(null);
                                //    });
                            }
                            else {
                                console.warn('Refresh token is no more valid.');
                                this.tokenRenew.endUpdate(null);
                                resolve(null);
                            }
                        })
                        .catch((error) => {
                            console.error('Error while refreshing token: ' + error);
                            this.tokenRenew.endUpdate(null);
                            resolve(null);
                        });
                }
                else {
                    this.tokenRenew.endUpdate(null);
                    resolve(null);
                }
            });
        }

        // Aguarda o primeiro request de novo login terminar.
        return new Promise<string>((resolve) => {

            var id = setInterval(() => {
                if (!this.tokenRenew.isUpdating) {
                    resolve(this.tokenRenew.newToken);
                    clearInterval(id);
                }
            }, 1000);

        });
    }

    ///
    private sessionExpiredRedirect() {
        console.info("Login Expired while refreshing token.");
        Session.clearSession();
        this.props.history.replace(RoutePaths.LoginExpired);
    }

    ////////////////////////////////////////////////////////

    public componentDidMount() {

        // Set axios interceptors
        //this.requestInterceptor = axios.interceptors.request.use(req => {
        //    this.setState({ error: null, statusCode: null });
        //    return req;
        //});

        this.responseInterceptor = axios.interceptors.response.use(
            res => res,
            error => {
                const debug = HtmlUtils.isLocalhost();
                const response = error.response as AxiosResponse;
                const originalRequest = error.config as AxiosRequestConfig;
                const apiException: ApiException = response && response.data ? (response.data as ApiException) : null;
                const authErrors = response && response.headers ? response.headers["www-authenticate"] as string : undefined;
                const ignoreErrors = originalRequest && originalRequest.headers && originalRequest.headers["X-Custom-AxiosIgnoreErrors"] === "1";
                const serverErrorReport = debug && error && error.config ? ` (Url: '${error.config.url}' - ${error.message})` : "";

                if (originalRequest
                    && response
                    && response.status === 401
                    && authErrors != undefined
                    && (authErrors.indexOf("invalid_token") > -1 || authErrors.indexOf("Bearer") > -1 || authErrors.indexOf("ClaimPrincipal_NOT_VALID") > -1)) {

                    return this.updateToken()
                        .then((token) => {
                            if (token != null) {
                                if (Session.bearerAuthorization != null) originalRequest.headers["Authorization"] = Session.bearerAuthorization;
                                return axios.request(originalRequest);
                            }
                            else {
                                this.sessionExpiredRedirect();
                                throw error;
                            }
                        });
                }

                // Cancelling uploading?
                if (!axios.isCancel(error)) {

                    const mapErrorToApiException: ApiException =
                    {
                        exceptionType: `(${response.status})`,
                        exceptionMessage: this.props.t("Components.AxiosHandler.Response.Default") + serverErrorReport,
                        errorList: error.toString(),
                        exceptionCode: response.status,
                        stackTrace: null,
                        isFatal: false
                    };

                    let newState = this.state;

                    if (response == null) {
                        newState = {
                            error: {
                                exceptionType: "axios",
                                exceptionMessage: this.props.t("Components.AxiosHandler.Response.None") + serverErrorReport,
                                exceptionCode: -1,
                                errorList: null,
                                stackTrace: null,
                                isFatal: false
                            }
                        };
                    }
                    else {

                        if (response.status == 0 || response.status == 503) {
                            if (!ignoreErrors) this.props.history.replace(RoutePaths.ServerNotAvailable);
                        }
                        else if (response.status == 401
                            && authErrors != undefined
                            && (authErrors.indexOf("invalid_token") > -1 || authErrors.indexOf("Bearer") > -1)) {
                            this.sessionExpiredRedirect();
                        }
                        else if (apiException && apiException.exceptionType == "AccessDeniedException")
                            this.props.history.replace(RoutePaths.AccessDenied);
                        else {
                            switch (response.status) {
                                case 400:
                                    newState = {
                                        error: {
                                            exceptionType: "Bad Request",
                                            exceptionMessage: this.props.t("Components.AxiosHandler.Response.400") + serverErrorReport,
                                            exceptionCode: response.status,
                                            errorList: null,
                                            stackTrace: null,
                                            isFatal: false
                                        }
                                    };
                                    break;

                                case 404:
                                    newState = {
                                        error: {
                                            exceptionType: "XHR",
                                            exceptionMessage: this.props.t("Components.AxiosHandler.Response.404") + serverErrorReport,
                                            exceptionCode: response.status,
                                            errorList: null,
                                            stackTrace: null,
                                            isFatal: false
                                        }
                                    };
                                    break;

                                case 406: // (AppRuleValidationException)
                                case 409: // (AppException)
                                case 500: // (Outras Exceções)
                                    newState = { error: (apiException == null) ? mapErrorToApiException : apiException };
                                    break;

                                default:
                                    newState = { error: mapErrorToApiException };
                                    break;
                            }
                        }
                    }

                    // Adiciona propriedades ao HTTP ERROR.
                    error["handledError"] = newState.error;
                    error["handled"] = false;

                    // Define como o erro será informado ao usuário
                    if (!ignoreErrors
                        && newState.error.exceptionCode != 406
                        && newState.error.exceptionCode != 409
                        && newState.error.exceptionCode < 500
                    ) {
                        this.setState({ ...newState });
                        error["handled"] = true;
                    }

                    // Informa o Erro ao Componente Chamador.
                    throw error;
                }
                else {
                    const newError = { ...error, canceled: true };
                    throw newError;
                }
            });
    }

    public componentWillUnmount() {
        // Remove handlers, so Garbage Collector will get rid of if WrappedComponent will be removed 
        axios.interceptors.request.eject(this.requestInterceptor);
        axios.interceptors.response.eject(this.responseInterceptor);
    }

    public render() {
        return (
            <RequestErrorDialog
                open={this.state.error != null}
                error={this.state.error}
                onClose={() => this.setState({ error: null }, () => document.dispatchEvent(new Event('RequestErrorDialogClose')))}
            />
        );
    }
};

export default withTranslation()(withRouter(withSnackbar(AxiosHandler)));